import {
    ModalFooter,
    ModalHeader,
} from 'components/global/CalendarModal/styles'
import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Calendar from 'components/shared/Calendar'
import { CalendarLoader } from 'components/shared/Calendar/components/CalendarLoader'
import Modal from 'components/shared/Modal'
import Text from 'components/shared/Text'
import { formatDates } from 'helpers/dates'
import { closeModalActions } from 'helpers/helpers'
import moment, { Moment } from 'moment'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useGetCalendarByProductTypeQuery } from 'store/api-slice/hooks'
import { useCart } from 'store/cart-slice/hooks'
import { useResolutions } from 'store/resolutions'
// import { usePromotions } from 'components/shared/Promotions/hooks'
import CalendarLegendItem from 'components/global/CalendarModal/components/CalendarModalLegend/components/CalendarLegendItem'
import Info from 'components/shared/Info'
import { useApp } from 'store/steps'

interface SpaCalendarProps {
    date: string | null
    onChange: (value: string) => void
    onClose: () => void
    isOpened: boolean
}

const SpaCalendarModal = ({
    date,
    onChange,
    onClose,
    isOpened,
}: SpaCalendarProps) => {
    const { isMobile } = useResolutions()
    const { isStepEmpty } = useCart()
    const { language } = useApp()
    // TODO: connect with promotions
    // const { isDarkThemeForCabanas } = usePromotions()
    const isDarkThemeForSpa = false

    const [isCalendarLocked, setIsCalendarLocked] = useState(true)
    const [isSelectionConfirmed, setIsSelectionConfirmed] = useState(false)
    const [unconfirmedDates, setUnconfirmedDates] = useState<string[]>(
        date ? [date] : []
    )

    const { data, isFetching, isError } = useGetCalendarByProductTypeQuery(
        {
            _locale: language,
            productType: 'spa_treatment',
        },
        {
            skip: !isOpened,
        }
    )

    const handleSetDates = (dates: Moment[]) => {
        setUnconfirmedDates([dates[0].format('YYYY-MM-DD')])
        if (!isMobile) {
            setIsSelectionConfirmed(true)
            onChange(dates[0].format('YYYY-MM-DD'))
        }
    }

    const confirmDates = () => {
        onChange(unconfirmedDates[0])

        setTimeout(() => {
            setIsCalendarLocked(false)
            setIsSelectionConfirmed(false)
            closeModalActions(false)
            onClose()
        }, 500)
    }

    useEffect(() => {
        if (isSelectionConfirmed) {
            setIsCalendarLocked(true)
            confirmDates()
        }
    }, [isSelectionConfirmed])

    useEffect(() => {
        if (isFetching || isError || !data) return
        setIsCalendarLocked(false)
    }, [isFetching, isError, data])

    return (
        <Modal
            isOpened={isOpened}
            maxWidth={786}
            onClose={onClose}
            isMobileFullscreen={true}
            testIdPrefix="spa-calendar"
        >
            <>
                <ModalHeader hasDarkMode={isDarkThemeForSpa} isFullWidth>
                    <Text
                        variant="body"
                        mb={4}
                        color={
                            isDarkThemeForSpa
                                ? 'grayscale_500'
                                : 'grayscale_900'
                        }
                    >
                        <FormattedMessage
                            id="upsells.wellness.calendar_treatment_label"
                            defaultMessage="Data zabiegu"
                        />
                    </Text>

                    {date ? (
                        <Box direction="row" gap={4}>
                            <Text
                                variant="bodyLarge"
                                color={
                                    isDarkThemeForSpa
                                        ? 'grayscale_300'
                                        : 'secondary_500'
                                }
                            >
                                {formatDates([date])}
                            </Text>
                        </Box>
                    ) : (
                        <Text
                            variant="bodyLarge"
                            color={
                                isDarkThemeForSpa
                                    ? 'grayscale_300'
                                    : 'secondary_500'
                            }
                        >
                            <FormattedMessage
                                id="upsells.wellness.calendar_treatment_placeholder"
                                defaultMessage="Wybierz datę zabiegu"
                            />
                        </Text>
                    )}
                </ModalHeader>
                {!data ? (
                    <>
                        {isFetching && <CalendarLoader />}
                        {isError && (
                            <Box
                                minHeight={530}
                                align="center"
                                justify="center"
                                isFullWidth
                            >
                                <Box width={['100%', 380]}>
                                    <Info
                                        color="info_warning"
                                        variantType="error"
                                        align="center"
                                        isFullWidthContent
                                        hasNoBackground
                                    >
                                        <Text
                                            variant="bodySmall"
                                            color="grayscale_900"
                                        >
                                            <FormattedMessage
                                                id="global.error.generic_technical_issue"
                                                defaultMessage="Napotkaliśmy problem techniczny. Odśwież stronę lub spróbuj ponownie później."
                                            />
                                        </Text>
                                    </Info>
                                </Box>
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <Box>
                            <Calendar
                                mode="buses"
                                handleDatesChosen={handleSetDates}
                                datesInfo={data ? data : null}
                                isLimitedRange={true}
                                isDisabled={isCalendarLocked}
                                date={date ? moment(date) : null}
                                testIdPrefix="spa"
                                hasDarkMode={isDarkThemeForSpa}
                            />
                        </Box>
                        <ModalFooter
                            gap={16}
                            align="flex-start"
                            hasDarkMode={isDarkThemeForSpa}
                            isFullWidth
                        >
                            {(!isStepEmpty('tickets') ||
                                !isStepEmpty('accommodation')) && (
                                <CalendarLegendItem
                                    badge="highlight"
                                    description={
                                        <>
                                            {!isStepEmpty('tickets') &&
                                                isStepEmpty(
                                                    'accommodation'
                                                ) && (
                                                    <FormattedMessage
                                                        id="global.calendar.legend_staying_days"
                                                        defaultMessage="Dni Twojego pobytu w parku"
                                                    />
                                                )}
                                            {!isStepEmpty('accommodation') && (
                                                <FormattedMessage
                                                    id="global.calendar.legend_village_staying_days"
                                                    defaultMessage="Dni Twojego pobytu w Suntago Village"
                                                />
                                            )}
                                        </>
                                    }
                                />
                            )}
                            {isMobile && (
                                <Button
                                    onClick={confirmDates}
                                    isDisabled={unconfirmedDates.length === 0}
                                >
                                    <FormattedMessage
                                        id="global.btn.confirm"
                                        defaultMessage="Potwierdź"
                                    />
                                </Button>
                            )}
                        </ModalFooter>
                    </>
                )}
            </>
        </Modal>
    )
}

export default SpaCalendarModal
